
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import HeaderTitle from '@/components/HeaderTitle.vue';
import ReportFilter from '@/components/ReportFilter.vue';
import DateFilter from '@/components/DateFilter.vue';
import LocationFilter from '@/components/LocationFilter.vue';
import ReportSort from '@/components/ReportSort.vue';
import ReorderUserParams from '@/components/ReorderUserParams.vue';
import { SupplierModel } from '@/models/supplier';
import { SupplierList } from '@/collections/suppliers';
import { StoreLocationModel } from '@/models/store_location';
import { FilterColumnModel } from '@/models/filter_column';
import { StoreLocationList } from '@/collections/store_locations';
import { FilterColumnList } from '@/collections/filter_columns';
import { ReportModel } from '@/models/report';
import { PurchaseOrderModel, IPurchaseOrderItem } from '@/models/purchase_order';
import { PurchaseOrders } from '@/collections/purchase_orders';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import { ParamList } from '@/collections/params';
import { StoreModel } from '@/models/store';
import { MeasureList } from '@/collections/measures';
import { AnalyticsTypeModel } from '@/models/analytics_type';
import { ProductSegmentModel } from '@/models/product_segment';
import { EventBus } from '@/main';
import DataSyncStatusBanner from '@/components/DataSyncStatusBanner.vue';
import { SortableColumn } from '@/types/sort';
import {
  BUYING_TABLE_SORTABLE_COLUMNS,
  BUYING_TABLE_PRODUCT_INFO_SORT_CODES,
} from '@/views/inventory/buyingTableSortColumns';
import { getSuggestedQtyColor } from '@/views/inventory/buyingTableColors';
import DownloadFullData from '@/components/DownloadFullData.vue';
import ActionButton from '@/components/ActionButton.vue';
import { sanitizeNumber } from '@/utils/number';
import { reviewRequestService } from '@/services/ReviewRequestService';
import { TaxTypeList } from '@/collections/tax_types';
import { TaxTypeModel } from '@/models/tax_type';
import { CurrencyHelper } from '@/helpers/CurrencyHelper';

interface Product {
  variant_id: string;
  productName: string;
  variantName: string;
  sku: string;
  currentInventory: number;
  reorderQuantity: number;
  reorderDate: string;
  supplier_id: string;
  supplier: string;
  unitsSoldPerDay: number;
  unitsSold: number;
  inventoryRemainingDays: number;
}

interface FilterOptions {
  category: string;
  inventoryStatus: string;
  fromDate: string;
  toDate: string;
}

interface FilterData {
  dimension: DimensionModel;
  analyticsType: AnalyticsTypeModel;
  dates: string[];
  filterColumns: FilterColumnList;
  reportParams: ParamList;
  productSegment: ProductSegmentModel;
}

interface PageOptions {
  sortBy?: any;
  sortDesc?: any;
  page?: string;
  itemsPerPage?: string;
  multiSort?: boolean;
}

@Component({
  components: {
    HeaderTitle,
    ReportFilter,
    DateFilter,
    DataSyncStatusBanner,
    LocationFilter,
    ReportSort,
    ReorderUserParams,
    DownloadFullData,
    ActionButton,
  },
})
export default class BuyingTable extends Vue {
// Add new properties for ReportFilter
  @Prop({ default: () => new StoreModel() })
  public store!: StoreModel;
  @Prop({ default: () => new MeasureList() })
  public measures!: MeasureList;
  public measure: MeasureModel = new MeasureModel();
  public dimension: DimensionModel = new DimensionModel();
  public analyticsType: AnalyticsTypeModel = new AnalyticsTypeModel();
  public readyToGetReport: boolean = false;
  public needRefreshData: boolean = false;
  public dates: string[] = []; // For date range from ReorderUserParams
  @Prop({ default: false })
  public enableAthena!: boolean;
  @Prop({ default: false })
  public isDataReady!: boolean;
  @Prop({ default: false })
  public isAthenaReady!: boolean;
  @Prop({ default: false })
  public isAthenaFinished!: boolean;
  @Prop({ default: () => [] })
  public timeRangeLimit!: string[];
  @Prop({ default: false })
  public hasSchemas!: boolean;
  public filterColumns: FilterColumnList = new FilterColumnList();
  public initFilterColumns: FilterColumnList = new FilterColumnList();
  public reportParams: ParamList = new ParamList();
  public initReportParams: ParamList = new ParamList();
  public productSegment: ProductSegmentModel = new ProductSegmentModel();
  public filterReportColumns: any[] = [];
  public availableParams: ParamList = new ParamList();
  public reportColumns: any = [];
  public initReportColumns: any = [];
  public initReportFilterColumns: any = [];
  // Data properties
  public selectedDeliveryLocationId: string | null = null;
  public loadingLocations = false;
  public selectedProducts: any[] = [];
  public totalItems = 0;
  public filterDialog = false;
  public purchaseOrderDialog = false;
  public productDetailsDialog = false;
  public selectedProductForDetails: any = null;
  public exportDialog = false;
  public downloadFileExt = 'csv';

  // Incoming breakdown dialog
  public incomingBreakdownDialog = false;
  public incomingBreakdownLoading = false;
  public incomingBreakdownData: any = null;
  public selectedVariantForIncoming: any = null;
  fromDateMenu = false;
  toDateMenu = false;
  selectedSupplier = '';
  supplierFilter = '';
  suppliers: SupplierList = new SupplierList();
  locations: StoreLocationModel[] = [];
  loadingSuppliers = false;
  private taxTypeList: TaxTypeList = new TaxTypeList();
  loading = false;

  // Filter states
  filters: FilterOptions = {
    category: '',
    inventoryStatus: '',
    fromDate: '',
    toDate: ''
  };

  // Dropdown options
  supplierOptions = [
    { text: 'All Suppliers', value: 'all' }
  ];

  categoryOptions = [
    { text: 'All Categories', value: '' },
    { text: 'Electronics', value: 'electronics' },
    { text: 'Clothing', value: 'clothing' },
    { text: 'Home & Kitchen', value: 'home_kitchen' }
  ];

  inventoryStatusOptions = [
    { text: 'All Status', value: '' },
    { text: 'Out of Stock', value: 'out_of_stock' },
    { text: 'Low Stock', value: 'low_stock' },
    { text: 'In Stock', value: 'in_stock' }
  ];

  // Table headers - responsive widths
  tableHeaders: DataTableHeader[] = [
    { text: 'Product', value: 'productInfo', sortable: false},
    { text: 'Inventory Status', value: 'inventoryStatus', sortable: false},
    {
      text: 'Suggested',
      value: '[inventory_replenishment]',
      align: 'start',
      sortable: false
    },
  ];

  // Mock products (will be replaced with API data)
  products: Product[] = [];
  public filterData: FilterData = {
    dimension: new DimensionModel(),
    analyticsType: new AnalyticsTypeModel(),
    dates: [],
    filterColumns: new FilterColumnList(),
    reportParams: new ParamList(),
    productSegment: new ProductSegmentModel(),
  };

  recentlyCreatedPOs: PurchaseOrderModel[] = [];

  // Add to component data
  creatingAllLists: boolean = false;

  private pageOptions: PageOptions = {
    page: '1',
    itemsPerPage: '50',
    sortBy: ['[reorder_date]', '[inventory_replenishment]'],
    sortDesc: [false, true],
    multiSort: false
  };

  // Add new property for initial loading state
  private initialLoading = true;

  // Add new properties for DateFilter


  // Add readiness flags
  private componentReady: boolean = false;
  private dataSyncReady: boolean = false;

  // Flag to prevent watchers from triggering duplicate fetches during initial load
  private filtersInitialized: boolean = false;

  // Pending product segment ID from localStorage, validated asynchronously before use
  private pendingProductSegmentId: number | null = null;

  // Add new property for active location filters
  activeLocationFilters: FilterColumnList | null = null;

  // NEW: Property for reorder filter checkbox
  private showNeedReorderNowOnly: boolean = true;

  // Computed property for sortable columns configuration.
  // The list (and per-column ASC overrides for urgency columns) lives in
  // buyingTableSortColumns.ts so it can be unit-tested without instantiating
  // this component.
  public get sortableColumns(): SortableColumn[] {
    return BUYING_TABLE_SORTABLE_COLUMNS;
  }

  // Column codes the productInfo header's sort arrow should light up for.
  public get productInfoSortCodes(): string[] {
    return BUYING_TABLE_PRODUCT_INFO_SORT_CODES;
  }

  // Product details dialog headers
  public get productDetailsHeaders(): DataTableHeader[] {
    return [
      { text: 'Property', value: 'property', width: '40%' },
      { text: 'Value', value: 'value', width: '60%' }
    ];
  }

  // Product details items for the dialog table
  public get productDetailsItems(): any[] {
    if (!this.selectedProductForDetails) return [];

    const product = this.selectedProductForDetails;
    const items = [];

    // Note: Inventory Status section removed - now shown in timeline visualization above

    // === SETTINGS (User-configured inputs) ===
    items.push({ group: 'sales_forecast', property: 'Lead Time', formula: 'Days from order placement to inventory receipt. Configure in supplier settings.', value: product.vendorLeadTime || 30, key: 'lead_time', subgroup: 'settings', icon: 'mdi-cog-outline', suffix: 'days' });
    items.push({ group: 'sales_forecast', property: 'Coverage Days', formula: 'Reorder cycle - days this order should cover until next reorder. Based on min/max stock cover settings.', value: product.coverageDays || 0, key: 'coverage_days', subgroup: 'settings', suffix: 'days' });

    // === SALES RATE (Daily sales rates) ===
    items.push({ group: 'sales_forecast', property: 'Historical Daily Sales', formula: 'Average daily sales based on historical data.', value: product.unitsSoldPerDay || 0, key: 'sales_rate', subgroup: 'sales_rate', icon: 'mdi-chart-line', suffix: 'units/day' });
    items.push({ group: 'sales_forecast', property: 'Forecast Daily Sales (Lead Time)', formula: 'Predicted daily sales during lead time period. Accounts for trends and seasonality.', value: product.forecastUnitsSoldPerDay || 0, key: 'forecast_daily', subgroup: 'sales_rate', suffix: 'units/day' });
    items.push({ group: 'sales_forecast', property: 'Forecast Daily Sales (Coverage)', formula: 'Predicted daily sales during coverage period. May differ due to seasonality.', value: product.forecastDailySalesCoverage || 0, key: 'forecast_daily_coverage', subgroup: 'sales_rate', suffix: 'units/day' });

    // === DEMAND FORECAST (Projected demand for each period) ===
    items.push({ group: 'sales_forecast', property: 'Lead Time Demand', formula: '[Forecast Daily Sales (Lead Time)] × [Lead Time]', value: product.forecastLeadTimeDemand || 0, key: 'forecast_lead_demand', subgroup: 'demand', icon: 'mdi-chart-bar', suffix: 'units' });
    items.push({ group: 'sales_forecast', property: 'Coverage Demand', formula: '[Forecast Daily Sales (Coverage)] × [Coverage Days]', value: product.forecastNextCycleDemand || 0, key: 'forecast_next_demand', subgroup: 'demand', suffix: 'units' });

    // === INVENTORY PROJECTION (Projected stock levels & triggers) ===
    items.push({ group: 'sales_forecast', property: 'Total Inventory Need', formula: '[Coverage Demand] + [Safety Stock]', value: Math.round((product.safetyStock || 0) + (product.forecastNextCycleDemand || 0)), key: 'forecast_inventory_need', subgroup: 'projection', icon: 'mdi-package-variant', suffix: 'units', highlight: true });
    items.push({ group: 'sales_forecast', property: 'Reorder Point', formula: '[Safety Stock] + [Lead Time Demand]', value: product.reorderPoint || 0, key: 'reorder_point', subgroup: 'projection', suffix: 'units' });

    // === ORDER RECOMMENDATION (Final decisions & output) ===
    items.push({ group: 'sales_forecast', property: 'Reorder Date', formula: 'Date when inventory reaches reorder point.', value: product.reorderDate || 'N/A', key: 'reorder_date', subgroup: 'recommendation', icon: 'mdi-cart-check' });
    items.push({ group: 'sales_forecast', property: 'Coverage Start', formula: '[Reorder Date] + [Lead Time]', value: product.coverageStartDate || 'N/A', key: 'coverage_start', subgroup: 'recommendation' });
    items.push({ group: 'sales_forecast', property: 'Coverage End', formula: '[Coverage Start] + [Coverage Days]', value: product.coverageEndDate || 'N/A', key: 'coverage_end', subgroup: 'recommendation' });
    // Split incoming inventory by ETA vs lead time
    const incomingBefore = product['[incoming_before_lead_time]'] || product.incomingBeforeLeadTime || 0;
    const incomingAfter = product['[incoming_after_lead_time]'] || product.incomingAfterLeadTime || 0;
    items.push({ group: 'sales_forecast', property: 'Incoming (Before Lead Time)', formula: 'Units arriving before lead time ends. Adds to effective inventory, extends days remaining.', value: incomingBefore, key: 'incoming_before', subgroup: 'recommendation', suffix: 'units' });
    items.push({ group: 'sales_forecast', property: 'Incoming (After Lead Time)', formula: 'Units arriving after lead time. Reduces reorder quantity needed.', value: incomingAfter, key: 'incoming_after', subgroup: 'recommendation', suffix: 'units' });
    items.push({ group: 'sales_forecast', property: 'Suggested Qty', formula: '[Total Inventory Need] − [Incoming After Lead Time]', value: Math.max(0, Math.round((product.safetyStock || 0) + (product.forecastNextCycleDemand || 0)) - incomingAfter), key: 'suggested', subgroup: 'recommendation', suffix: 'units', highlight: true });
    if (product['[case_quantity]'] && parseFloat(product['[case_quantity]']) > 0) {
      items.push({
        group: 'sales_forecast',
        property: 'Case Pack Size',
        formula: 'Units per case/pack for rounding.',
        value: Math.round(product['[case_quantity]']) || 0,
        key: 'case_quantity',
        subgroup: 'recommendation',
        suffix: 'units'
      });
      items.push({
        group: 'sales_forecast',
        property: 'Suggested Qty (Rounded)',
        formula: 'Quantity rounded up to full cases.',
        value: Math.round(product['[inventory_replenishment_rounded]'] || product['[inventory_replenishment]'] || 0),
        key: 'suggested_rounded',
        subgroup: 'recommendation',
        suffix: 'units',
        highlight: true
      });
    }
    // Additional metrics
    //items.push({ property: 'Units Per Case', value: product.unitsPerCase || 0, key: 'case_qty' });
    // items.push({ property: 'Min Order Quantity', value: product.minOrderQuantity || 0, key: 'min_order' });
    // items.push({ property: 'Max Order Quantity', value: product.maxOrderQuantity || 0, key: 'max_order' });
    items.push({ group: 'inventory_config', property: 'Fixed Order Quantity', value: product.fixedOrderQuantity || null, key: 'fixed_order' });
    items.push({ group: 'inventory_config', property: 'Min Order Qty', value: product.variantMinOrderQuantity || null, key: 'variant_min' });
    items.push({ group: 'inventory_config', property: 'Max Order Qty', value: product.variantMaxOrderQuantity || null, key: 'variant_max' });
    items.push({ group: 'inventory_config', property: 'Min Inventory Quantity', value: product.minInventoryQuantity || null, key: 'min_inventory' });
    items.push({ group: 'inventory_config', property: 'Max Inventory Quantity', value: product.maxInventoryQuantity || null, key: 'max_inventory' });
    items.push({ group: 'inventory_config', property: 'Min Stock Cover Days', value: product.minStockCoverDays || null, key: 'min_cover_days' });
    items.push({ group: 'inventory_config', property: 'Max Stock Cover Days', value: product.maxStockCoverDays || null, key: 'max_cover_days' });
    //items.push({ property: 'Safety Stock Cover Days', value: product.safetyStockCoverDays || 0, key: 'safety_cover_days' });
    //items.push({ property: 'Days to Reorder', value: product.daysToReorder || 0, key: 'days_to_reorder' });
    //items.push({ property: 'Reorder Priority', value: product.reorderPriority || 'normal', key: 'priority' });

    // Financial metrics
    // items.push({ property: 'Product Profit Margin', value: product.productProfitMargin || 0, key: 'margin' });
    // items.push({ property: 'Replenishment Cost', value: product.replenishmentInventoryCost || 0, key: 'replenishment_cost' });
    // items.push({ property: 'Replenishment Revenue', value: product.replenishmentRevenue || 0, key: 'replenishment_revenue' });
    // items.push({ property: 'Replenishment Profit', value: product.replenishmentProfit || 0, key: 'replenishment_profit' });
    // items.push({ property: 'Net Sales', value: product.netSales || 0, key: 'net_sales' });
    // items.push({ property: 'Sales Contribution', value: product.salesContribution || 0, key: 'sales_contribution' });
    // items.push({ property: 'Sales Contribution Accumulate', value: product.salesContributionAccumulate || 0, key: 'sales_contribution_acc' });

    // Performance metrics
    //items.push({ property: 'Out of Stock Days', value: product.outOfStockDays || 0, key: 'out_of_stock_days' });

    return items;
  }

  // Product details grouped by category
  public get productDetailsGroups(): any[] {
    if (!this.selectedProductForDetails) return [];

    const items = this.productDetailsItems;
    const groups: { [key: string]: any } = {};

    // Define group metadata
    // Note: 'inventory' and 'replenishment_info' groups removed - merged into sales_forecast with timeline visualization
    const groupMeta: { [key: string]: any } = {
      'product_info': {
        title: 'Core Product Information',
        icon: 'mdi-information',
        color: '#1976D2'
      },
      'sales_forecast': {
        title: 'Replenishment Timeline',
        icon: 'mdi-timeline',
        color: '#59C8E4'
      },
      'inventory_config': {
        title: 'Inventory Configuration',
        icon: 'mdi-cog',
        color: '#9C27B0'
      },
      'financial': {
        title: 'Financial Analysis',
        icon: 'mdi-currency-usd',
        color: '#2E7D32'
      }
    };

    // Group items by their group attribute
    items.forEach((item: any) => {
      const groupKey = item.group || 'other';
      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: groupKey,
          title: groupMeta[groupKey]?.title || groupKey.replace('_', ' ').toUpperCase(),
          icon: groupMeta[groupKey]?.icon || 'mdi-folder',
          color: groupMeta[groupKey]?.color || '#9E9E9E',
          items: []
        };
      }
      groups[groupKey].items.push(item);
    });

    return Object.values(groups);
  }

  // Default open panels - opens Replenishment Timeline group by default
  public get defaultOpenPanels(): number[] {
    const groups = this.productDetailsGroups;
    const forecastGroupIndex = groups.findIndex(group =>
      group.name === 'sales_forecast'
    );
    return forecastGroupIndex >= 0 ? [forecastGroupIndex] : [0];
  }

  // Inventory projection computed properties for timeline visualization
  public get inventoryProjections(): any {
    if (!this.selectedProductForDetails) return null;

    const product = this.selectedProductForDetails;
    // Ensure numeric conversion to prevent string concatenation bugs
    const currentStock = Number(product.availableQuantity) || 0;
    // Split incoming inventory by ETA vs lead time
    // incoming_before_lead_time: arrives before a new PO would (adds to effective inventory)
    // incoming_after_lead_time: arrives after a new PO would (reduces reorder qty, added at restock)
    const incomingBeforeLeadTime = Number(product['[incoming_before_lead_time]'] || product.incomingBeforeLeadTime) || 0;
    const incomingAfterLeadTime = Number(product['[incoming_after_lead_time]'] || product.incomingAfterLeadTime) || 0;
    const incomingQuantity = Number(product.incomingQuantity) || (incomingBeforeLeadTime + incomingAfterLeadTime) || 0;
    const safetyStock = Number(product.safetyStock) || 0;
    const forecastLeadTimeDemand = Number(product.forecastLeadTimeDemand) || 0;
    const forecastNextCycleDemand = Number(product.forecastNextCycleDemand) || 0;
    const suggestedQuantity = Math.round(Number(product['[inventory_replenishment]'] || product.reorderQuantity) || 0);
    const vendorLeadTime = Number(product.vendorLeadTime) || 30;
    const coverageDays = Number(product.coverageDays || product.reorderCycle) || vendorLeadTime;

    // Sanitize sales values to prevent negative, NaN, or Infinity from causing calculation errors
    const historicalDailySales = sanitizeNumber(product.unitsSoldPerDay);
    const forecastDailySalesLeadTime = sanitizeNumber(product.forecastUnitsSoldPerDay);
    const forecastDailySalesCoverage = sanitizeNumber(product.forecastDailySalesCoverage) || forecastDailySalesLeadTime;

    // Calculate dates first
    const today = new Date();

    // Reorder point
    const reorderPoint = Number(product.reorderPoint) || safetyStock;

    // Reorder date from product data or calculate from days until reorder point
    let reorderDate: Date;
    let daysToReorderPoint = 0;
    if (product.reorderDate) {
      reorderDate = new Date(product.reorderDate);
      daysToReorderPoint = Math.max(0, Math.ceil((reorderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
    } else {
      // Estimate: when will stock hit reorder point?
      daysToReorderPoint = forecastDailySalesLeadTime > 0
        ? Math.max(0, Math.floor((currentStock - reorderPoint) / forecastDailySalesLeadTime))
        : 0;
      reorderDate = new Date(today);
      reorderDate.setDate(reorderDate.getDate() + daysToReorderPoint);
    }

    // Projected inventory at reorder date (Step 2: Today → Reorder)
    const forecastDemandToReorder = daysToReorderPoint * forecastDailySalesLeadTime;
    const inventoryAtReorder = Math.max(0, currentStock - forecastDemandToReorder);
    // Inventory at reorder including incoming that arrives before lead time
    const inventoryAtReorderWithIncoming = Math.max(0, currentStock + incomingBeforeLeadTime - forecastDemandToReorder);

    // Inventory at end of lead time when order arrives (Step 3: Reorder → Arrival)
    // Uses inventoryAtReorderWithIncoming as starting point (incoming before lead time already added)
    const inventoryAtArrival = Math.max(0, inventoryAtReorderWithIncoming - forecastLeadTimeDemand);

    // Inventory after restock (when new order arrives)
    // incoming_before_lead_time is already counted in effective inventory (extends days remaining)
    // incoming_after_lead_time arrives with/after the new order
    const inventoryAfterRestock = inventoryAtArrival + suggestedQuantity + incomingAfterLeadTime;

    // Inventory at end of coverage period / next reorder point (Step 4)
    const inventoryAtNextReorder = Math.max(0, inventoryAfterRestock - forecastNextCycleDemand);

    // Status indicators
    const isAboveSafetyAtArrival = inventoryAtArrival >= safetyStock;
    const isAboveSafetyAtNextReorder = inventoryAtNextReorder >= safetyStock;
    const willStockOutDuringLeadTime = inventoryAtArrival <= 0;

    // Days until stockout (from current stock + incoming before lead time)
    // Incoming before lead time extends days remaining since it arrives before a new PO would
    // Ensure never negative (min 0)
    const effectiveStock = currentStock + incomingBeforeLeadTime;
    const daysUntilStockout = effectiveStock <= 0
      ? 0
      : forecastDailySalesLeadTime > 0
        ? Math.max(0, Math.floor(effectiveStock / forecastDailySalesLeadTime))
        : 365;

    // Trend calculations (comparing forecast to historical)
    const leadTimeTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesLeadTime - historicalDailySales) / historicalDailySales) * 100)
      : 0;
    const coverageTrendPercent = historicalDailySales > 0
      ? Math.round(((forecastDailySalesCoverage - historicalDailySales) / historicalDailySales) * 100)
      : 0;

    // Risk level
    const riskLevel = willStockOutDuringLeadTime ? 'critical'
      : !isAboveSafetyAtArrival ? 'warning'
      : 'healthy';

    // Arrival date = reorder date + lead time
    const arrivalDate = new Date(reorderDate);
    arrivalDate.setDate(arrivalDate.getDate() + vendorLeadTime);

    // Next cycle end = arrival date + coverage days
    const nextCycleEnd = new Date(arrivalDate);
    nextCycleEnd.setDate(nextCycleEnd.getDate() + coverageDays);

    // Days from today to reorder
    const daysToReorder = Math.max(0, Math.ceil((reorderDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));

    return {
      currentStock,
      effectiveStock,
      incomingQuantity,
      incomingBeforeLeadTime,
      incomingAfterLeadTime,
      safetyStock,
      suggestedQuantity,
      vendorLeadTime,
      coverageDays,
      historicalDailySales,
      forecastDailySalesLeadTime,
      forecastDailySalesCoverage,
      forecastLeadTimeDemand,
      forecastNextCycleDemand,
      inventoryAtArrival,
      inventoryAfterRestock,
      inventoryAtNextReorder,
      isAboveSafetyAtArrival,
      isAboveSafetyAtNextReorder,
      willStockOutDuringLeadTime,
      daysUntilStockout,
      daysRemaining: daysUntilStockout,
      daysRemainingAtReorder: forecastDailySalesLeadTime > 0
        ? Math.floor(inventoryAtReorder / forecastDailySalesLeadTime)
        : 999,
      leadTimeTrendPercent,
      coverageTrendPercent,
      riskLevel,
      daysToReorder,
      reorderPoint,
      inventoryAtReorder,
      inventoryAtReorderWithIncoming,
      forecastDemandToReorder,
      // Formatted dates for display
      todayFormatted: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      reorderDateFormatted: reorderDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      arrivalDateFormatted: arrivalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      nextCycleEndFormatted: nextCycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      // Legacy (for backward compatibility)
      arrivalDate: arrivalDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      nextReorderDate: nextCycleEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      // Inventory need calculation
      totalInventoryNeed: forecastNextCycleDemand + safetyStock,
      // Raw calculated suggested quantity (before rounding)
      // Only incoming_after_lead_time is subtracted (incoming_before_lead_time extends days remaining instead)
      rawSuggestedQuantity: forecastNextCycleDemand + safetyStock - incomingAfterLeadTime,
      // Max inventory for progress bar
      maxInventory: product.maxInventoryQuantity || Math.max(currentStock, inventoryAfterRestock) * 1.2
    };
  }

  // Natural language explanations for the replenishment timeline
  public get timelineExplanations(): any {
    const p = this.inventoryProjections;
    if (!p) return null;

    const fmt = (n: number, decimals = 0) => this.formatNumber(n, decimals);

    // Build today explanation with incoming inventory details
    let todayExplanation: string;

    if (p.incomingBeforeLeadTime > 0) {
      // When there's incoming before lead time, explain effective stock
      const effectiveStock = p.currentStock + p.incomingBeforeLeadTime;
      if (p.daysUntilStockout > 365) {
        todayExplanation = `You have ${fmt(p.currentStock)} units in stock, plus ${fmt(p.incomingBeforeLeadTime)} units arriving soon (${fmt(effectiveStock)} effective). At your current sales rate, you have plenty of inventory coverage.`;
      } else {
        todayExplanation = `You have ${fmt(p.currentStock)} units in stock, plus ${fmt(p.incomingBeforeLeadTime)} units arriving soon. Combined (${fmt(effectiveStock)} effective), this gives you about ${fmt(p.daysUntilStockout)} days of stock at ${fmt(p.forecastDailySalesLeadTime, 1)} units/day.`;
      }
      // Add explanation for incoming after lead time if present
      if (p.incomingAfterLeadTime > 0) {
        todayExplanation += ` Additionally, ${fmt(p.incomingAfterLeadTime)} units arriving later will reduce how much you need to order.`;
      }
    } else {
      // No incoming before lead time
      if (p.daysUntilStockout > 365) {
        todayExplanation = `You have ${fmt(p.currentStock)} units in stock. At your current sales rate, you have plenty of inventory coverage.`;
      } else {
        todayExplanation = `You have ${fmt(p.currentStock)} units in stock. At ${fmt(p.forecastDailySalesLeadTime, 1)} units/day, this will last about ${fmt(p.daysUntilStockout)} days before running out.`;
      }
      // Add explanation for incoming after lead time if present
      if (p.incomingAfterLeadTime > 0) {
        todayExplanation += ` You have ${fmt(p.incomingAfterLeadTime)} units arriving later, which will reduce how much you need to order.`;
      }
    }

    return {
      today: todayExplanation,

      beforeReorder: p.daysToReorder > 0
        ? `Over the next ${fmt(p.daysToReorder)} days, you'll sell approximately ${fmt(p.forecastDemandToReorder)} units. ` +
          (p.incomingBeforeLeadTime > 0
            ? `With ${fmt(p.incomingBeforeLeadTime)} incoming units arriving, your stock will be ${fmt(p.inventoryAtReorderWithIncoming)} units when it's time to reorder.`
            : `Your stock will drop from ${fmt(p.currentStock)} to ${fmt(p.inventoryAtReorderWithIncoming)} units, reaching your reorder point.`)
        : null,

      placeOrder: p.daysToReorder > 0
        ? `When stock reaches ${fmt(p.inventoryAtReorderWithIncoming)} units (at or below reorder point of ${fmt(p.reorderPoint)}), it's time to order more. This buffer ensures you don't run out while waiting for delivery.`
        : `Your current stock of ${fmt(p.currentStock)} units is at or below your reorder point of ${fmt(p.reorderPoint)} units. Order now to avoid running out.`,

      awaitingDelivery: p.willStockOutDuringLeadTime
        ? `After ordering, it takes ${fmt(p.vendorLeadTime)} days for delivery. During this time, you'll sell about ${fmt(p.forecastLeadTimeDemand)} units. Warning: You may run out of stock before the order arrives!`
        : `After ordering, it takes ${fmt(p.vendorLeadTime)} days for delivery. During this time, you'll sell about ${fmt(p.forecastLeadTimeDemand)} units. You'll have ${fmt(p.inventoryAtArrival)} units remaining when the order arrives.`,

      stockReplenished: p.suggestedQuantity > 0
        ? `Your new order of ${fmt(p.suggestedQuantity)} units arrives` +
          (p.incomingAfterLeadTime > 0 ? `, plus ${fmt(p.incomingAfterLeadTime)} other incoming units` : '') +
          `, bringing total stock to ${fmt(p.inventoryAfterRestock)} units. This should last ${fmt(p.coverageDays)} days until the next reorder.`
        : p.incomingAfterLeadTime > 0
          ? `No new order needed. You have ${fmt(p.incomingAfterLeadTime)} incoming units arriving, bringing stock to ${fmt(p.inventoryAfterRestock)} units. This should last ${fmt(p.coverageDays)} days until the next reorder.`
          : `No new order needed at this time. Your current inventory is sufficient to cover the next ${fmt(p.coverageDays)} days.`,

      orderCalculation: `We calculate: demand during reorder cycle (${fmt(p.forecastNextCycleDemand)} units) + safety buffer (${fmt(p.safetyStock)} units)` +
        (p.incomingAfterLeadTime > 0 ? ` - incoming orders (${fmt(p.incomingAfterLeadTime)} units)` : '') +
        ` = ${fmt(p.suggestedQuantity)} units to order.`
    };
  }

  // Total selected products count for dialog header
  public get totalSelectedProducts(): number {
    return this.selectedProducts.length;
  }

  // Created lifecycle hook
  async created() {
    // Load saved filters/products from localStorage FIRST (synchronous, before any async)
    this.initializeFromLocalStorage();

    // Initialize required objects
    await this.initReportMeasure();

    await Promise.all([
      this.fetchSuppliers(),
      this.fetchLocations(),
      this.validateSavedProductSegment()
    ]);

    // NOTE: Don't call fetchFullData here - wait for params to load first
    // fetchFullData will be called from onReorderParamsLoaded
    // Keep initialLoading = true until params are loaded and data is fetched
    // Don't set componentReady here - wait for params to be loaded
    // this.componentReady = true;  // REMOVED - will be set in onReorderParamsLoaded
  }

  private openInteractiveGuide(): void {
    window.open('https://app.arcade.software/share/khNWZWyGO5HfvRtB4imx', '_blank');
  }

  public async initReportMeasure() {
    this.readyToGetReport = false;
    const measureCode = "inventory_replenishment";
    this.measure.code = measureCode;
    if (this.measures.items.length === 0) {
        await this.measure.fetch();
    } else {
      for (const item of this.measures.items) {
        if (item.code === measureCode) {
          this.measure = item;
          break;
        }
      }
    }

    for (const item of this.measure.dimensions.items) {
      if (item.code === "by_variant") {
        this.dimension = item;
        break;
      }
    }
    this.analyticsType = this.measure.analyticsTypes.items[0];
  }

  async fetchLocations() {
    this.loadingLocations = true;
    try {
      const storeLocationList = new StoreLocationList();
      await storeLocationList.fetch();
      this.locations = storeLocationList.items;
    } catch (error) {
      console.error('Failed to fetch locations', error);
      this.$emit('error', 'Failed to load store locations');
    } finally {
      this.loadingLocations = false;
    }
  }

  // Add this private method to handle common filter creation
  private getCommonFilters(): { filters: FilterColumnList; supplierId: string } {
    const filters = new FilterColumnList();

    // Use activeLocationFilters from LocationFilter component
    if (this.activeLocationFilters && this.activeLocationFilters.items.length > 0) {
      this.activeLocationFilters.items.forEach(filter => {
        filters.add(filter);
      });
    }

    // Add filters from filter component
    for (const filter of this.filterColumns.items) {
      filters.add(filter);
    }

    let supplierId = this.supplierFilter;
    if (supplierId === 'all') {
      supplierId = '0';
    }

    return { filters, supplierId };
  }

  // Update fetchProducts to use the common filter method
  async fetchProducts() {
    this.loading = true;
    try {
      const { filters, supplierId } = this.getCommonFilters();



      const supplier = new SupplierModel({ id: parseInt(supplierId) });
      const supplierFilter = supplier.id ? 'eq' : 'all';
      const reportOptions: any = {
        initialFilterColumns: filters,
        pageOptions: this.pageOptions,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly,
        initialReportParams: this.reportParams,
        productSegment: this.productSegment
      };

      // Add date range from ReorderUserParams component
      if (this.dates.length >= 2) {
        reportOptions.dateRange = {
          start: this.dates[0],
          end: this.dates[1]
        };
      }

      const reportModel: ReportModel = await supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        supplierFilter,
        reportOptions
      );

      this.initReportColumns = reportModel.getReportColumnList();
      this.reportColumns = reportModel.getReportColumnList();
      this.initReportFilterColumns = reportModel.getReportColumnList();

      this.products = reportModel.getReportRowsData();
      if (this.products.length > 0 && this.totalItems === 0) {
        this.totalItems = this.products.length;
      }

      // convert products from report data
      this.products = this.products.map((product: any) => {
        // Helper function to safely get value from product
        const getValue = (key: string, defaultValue: any = 0) => {
          return product.hasOwnProperty(key) ? product[key] : defaultValue;
        };

        const getNumericValue = (key: string, defaultValue: number = 0) => {
          const value = getValue(key, defaultValue);
          return Number(value) || defaultValue;
        };

        const quantity = Number(Math.round(
          getValue('reorderQuantity', getValue('[inventory_replenishment]', 0))
        ));
        const unitCost = getNumericValue('[average_unit_cost]', 0);

        // Create standardized attributes from column codes
        const standardizedProduct: any = {
          ...product,
          variant_image: getValue('variant_image'),
          image: getValue('image'),
          reorderQuantity: Number(quantity),
          unitCost: unitCost,
          total: quantity * unitCost,
          supplier_id: getValue('supplier_id') && getValue('supplier_id') !== '-'
            ? getValue('supplier_id') : 'unknown',
          supplier: getValue('supplier') && getValue('supplier') !== '-'
            ? getValue('supplier') : 'unknown',
          unitsSoldPerDay: getNumericValue('[average_daily_units_sold_param]', 0),
          unitsSold: getNumericValue('[sold_quantity_param]', 0),
          inventoryRemainingDays: Math.round(getNumericValue('[stock_days_inventory_planning]', 0)),
          incomingQuantity: getNumericValue('[incoming_inventory_quantity]', 0),
          reorderDate: getValue('[reorder_date]', null),
          vendorLeadTime: getNumericValue('[vendor_lead_time]', 30),

          // Add standardized attributes from column codes with existence checks
          safetyStock: getNumericValue('[safety_stock_level]', 0),
          reorderPoint: getNumericValue('[reorder_point]', 0),
          availableQuantity: getNumericValue('[Actual Inventory Quantity]', 0),
          minInventoryQuantity: getNumericValue('[min_inventory_quantity]', 0),
          maxInventoryQuantity: getNumericValue('[max_inventory_quantity]', 0),
          minStockCoverDays: getNumericValue('[min_stock_cover_days]', 0),
          maxStockCoverDays: getNumericValue('[max_stock_cover_days]', 0),
          minOrderQuantity: getNumericValue('[min_order_quantity]', 0),
          maxOrderQuantity: getNumericValue('[max_order_quantity]', 0),
          fixedOrderQuantity: getNumericValue('[fixed_order_quantity]', 0),
          reorderCycle: getNumericValue('[reorder_cycle]', 0),
          reorderPriority: getValue('[reorder_priority]', 'normal'),
          forecastUnitsSoldPerDay: getNumericValue('[average_daily_units_sold_forecast]', 0),
          forecastDailySalesCoverage: getNumericValue('[average_daily_units_sold_forecast_next_cycle]', 0),
          forecastLeadTimeDemand: getNumericValue('[final_forecast_sales_current_cycle]', 0),
          forecastNextCycleDemand: getNumericValue('[final_forecast_sales_next_cycle]', 0),
          forecastOverUnderStock: getNumericValue('[forecast_over_under_stock_quantity]', 0),
          adjustedUnitsSoldPerDay: getNumericValue('[average_daily_units_sold_sales_trend]', 0),
          outOfStockDays: getNumericValue('[out_of_stock_days]', 0),
          productProfitMargin: getNumericValue('[product_profit_margin]', 0),
          replenishmentInventoryCost: getNumericValue('[replenishment_inventory_cost]', 0),
          replenishmentRevenue: getNumericValue('[replenishment_inventory_retail_value]', 0),
          replenishmentProfit: getNumericValue('[replenishment_inventory_gross_profit]', 0),
          sellingPrice: getNumericValue('[Selling Price]', 0),
          safetyStockCoverDays: getNumericValue('[safety_stock_days]', 0),
          daysToReorder: getNumericValue('[days_to_reorder]', 0),
          variantMinOrderQuantity: getNumericValue('[variant_min_order_quantity]', 0),
          variantMaxOrderQuantity: getNumericValue('[variant_max_order_quantity]', 0),
          forecastLostSales: getNumericValue('[forecast_lost_sales_quantity]', 0),
          netSales: getNumericValue('[net_sales]', 0),
          salesContributionAccumulate: getNumericValue('[sales_contribution_accmulate]', 0),
          salesContribution: getNumericValue('[sales_contribution]', 0),
          unitsPerCase: getNumericValue('[case_quantity]', 0)
        };

        // Calculate the date range that the reorder quantity covers
        // Start date: reorder date + vendor lead time
        // End date: start date + reorder cycle
        const reorderDateStr = standardizedProduct.reorderDate;
        const vendorLeadTime = standardizedProduct.vendorLeadTime || 30;
        const reorderCycle = standardizedProduct.reorderCycle || vendorLeadTime;

        if (reorderDateStr) {
          const reorderDate = new Date(reorderDateStr);

          // Calculate start date (reorder date + lead time)
          const coverageStartDate = new Date(reorderDate);
          coverageStartDate.setDate(coverageStartDate.getDate() + vendorLeadTime);

          // Calculate end date (start date + reorder cycle)
          const coverageEndDate = new Date(coverageStartDate);
          coverageEndDate.setDate(coverageEndDate.getDate() + reorderCycle);

          // Add formatted dates to the product
          standardizedProduct.coverageStartDate = coverageStartDate.toISOString().split('T')[0];
          standardizedProduct.coverageEndDate = coverageEndDate.toISOString().split('T')[0];
          standardizedProduct.coverageDateRange = `${standardizedProduct.coverageStartDate} to ${standardizedProduct.coverageEndDate}`;
          standardizedProduct.coverageDays = reorderCycle;
        } else {
          standardizedProduct.coverageStartDate = null;
          standardizedProduct.coverageEndDate = null;
          standardizedProduct.coverageDateRange = 'N/A';
          standardizedProduct.coverageDays = 0;
        }

        return standardizedProduct;
      });
    } catch (error) {
      console.error('Failed to fetch products', error);
      this.$emit('error', 'Failed to load products');
    } finally {
      this.loading = false;
    }
  }

  // Update fetchProductsCount to use the common filter method
  async fetchProductsCount() {
    try {
      const { filters, supplierId } = this.getCommonFilters();

      const supplier = new SupplierModel({ id: parseInt(supplierId) });
      const supplierFilter = supplier.id ? 'eq' : 'all';
      const countOptions: any = {
        initialFilterColumns: filters,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly,
        initialReportParams: this.reportParams,
        productSegment: this.productSegment
      };

      // Add date range from ReorderUserParams component
      if (this.dates.length >= 2) {
        countOptions.dateRange = {
          start: this.dates[0],
          end: this.dates[1]
        };
      }

      const count = await supplier.countProductsWithOptions(
        'inventory_replenishment',
        supplierFilter,
        countOptions
      );
      this.totalItems = Number(count.totalItems) || 0;
    } catch (error) {
      this.$emit('error', 'Failed to load product count');
    }
  }

  async fetchFullData() {
    this.loading = true;
    try {
      this.fetchProductsCount();
      await this.fetchProducts();
      this.saveBuyingTableFilters();
    } finally {
      this.loading = false;
    }
  }

  private get buyingTableFiltersKey(): string {
    return `buyingTable_filters_${this.store.id || 'default'}`;
  }

  private saveBuyingTableFilters(): void {
    const filters: any = {
      supplierFilter: this.supplierFilter || '',
      filterColumns: this.filterColumns.items.map(f => f.toJSON()),
      productSegmentId: this.productSegment?.id || null,
    };
    if (this.activeLocationFilters && this.activeLocationFilters.items.length > 0) {
      filters.locationFilters = this.activeLocationFilters.items.map(f => f.toJSON());
    }
    localStorage.setItem(this.buyingTableFiltersKey, JSON.stringify(filters));
  }

  // Custom filter method for the data table
  customFilter(value: any, search: string, item: any) {
    if (search.trim() === '') return true;

    // Search across product name, variant name, SKU
    const searchLower = search.toString().toLowerCase();
    return (
      item.productName?.toLowerCase().includes(searchLower) ||
      item.variantName?.toLowerCase().includes(searchLower) ||
      item.sku?.toLowerCase().includes(searchLower) ||
      item['Item[Item Description]']?.toLowerCase().includes(searchLower) ||
      item.product_sku?.toLowerCase().includes(searchLower)
    );
  }

  // Get color based on stock level
  getStockLevelColor(product: any): string {
    const stockLevel = product['[Actual Inventory Quantity]'] || product.currentInventory;
    const reorderQuantity = product['[inventory_replenishment]'] || product.reorderQuantity;

    // If we have less than half of the recommended reorder quantity, it's critical
    if (stockLevel <= (reorderQuantity * 0.5)) return 'error';

    // If we have less than the recommended reorder quantity, it's a warning
    if (stockLevel <= reorderQuantity) return 'warning';

    // Otherwise, we're good
    return 'success';
  }

  // Dialog methods
  openFilterDialog() {
    this.filterDialog = true;
  }

  openPurchaseOrderDialog() {
    // Clear recently created lists when opening the dialog
    this.generateProductsBySupplier();
    this.recentlyCreatedPOs = [];
    this.selectedDeliveryLocationId = null;
    this.purchaseOrderDialog = true;
  }

  resetFilters() {
    this.filters = {
      category: '',
      inventoryStatus: '',
      fromDate: '',
      toDate: ''
    };
  }

  async applyFilters() {
    if (!this.componentReady) return; // Add readiness check
    this.filterDialog = false;
    await this.fetchProducts();
  }

  addMoreProducts() {
    // Clear recently created lists when adding more products
    this.recentlyCreatedPOs = [];
    this.purchaseOrderDialog = false;
  }

  async createPurchaseOrderForSupplier(group: any) {
    group.loading = true;

    try {
      const products = group.products || [];
      if (!products.length) return;

      const supplier = group.supplier;
      if (!supplier) {
        throw new Error('Supplier not found');
      }

      if (!this.selectedDeliveryLocationId) {
        throw new Error('Delivery location is required');
      }

      const isUnknownSupplier = supplier.id === 'unknown';

      // --- CORRECTED: Calculate Expected Delivery Date ---
      let expectedDeliveryDateISOString: string | undefined = undefined;
      const leadTime = group.supplier.leadTime; // CORRECTED property name
      if (typeof leadTime === 'number' && leadTime >= 0) {
          const orderDate = new Date();
          const expectedDate = new Date(orderDate.setDate(orderDate.getDate() + leadTime));
          expectedDeliveryDateISOString = expectedDate.toISOString();
      }
      // --- END CORRECTED ---

      // Resolve supplier's default tax type
      let supplierDefaultTaxType: TaxTypeModel | null = null;
      const defaultTaxTypeId = supplier?.options?.financial?.default_tax_type_id;
      if (defaultTaxTypeId) {
        supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.id == defaultTaxTypeId) || null;
      }
      if (!supplierDefaultTaxType) {
        supplierDefaultTaxType = this.taxTypeList.items.find((t: TaxTypeModel) => t.isDefault) || null;
      }

      // --- Sub-task 6.3: Map Products to IPurchaseOrderItem[] ---
      // (Assuming group.products are the items to be included in this PO for this supplier)
      const supplierCurrencyCode = this.getSupplierCurrencyCode(supplier);
      const validPoItems: IPurchaseOrderItem[] = products.map((product: any) => {
        const orderedQuantity = this.formatNumber(product.reorderQuantity || product['[inventory_replenishment]'] || 0, 0);
        const qty = Number(orderedQuantity);
        const price = Number(product.unitCost) || 0;
        const taxExtra = supplierDefaultTaxType ? {
          financial: {
            tax_type_id: supplierDefaultTaxType.id,
            tax_type_name: supplierDefaultTaxType.name,
            tax_rate: supplierDefaultTaxType.rate,
            tax_amount: CurrencyHelper.roundForCurrency(qty * price * supplierDefaultTaxType.rate, supplierCurrencyCode),
          }
        } : undefined;
        return {
          variantId: product.variant_id,
          variantImage: product.variant_image,
          productName: product.productName || product['Item[Item Description]'] || 'Unknown Product',
          variant: product.variant || '',  // Add variant field
          sku: product.sku || product.product_sku || '',
          supplierSku: product.supplier_sku || product.supplierSku || undefined, // Add supplier SKU
          orderedQuantity: qty,
          receivedQuantity: 0, // Default for new PO items
          unitPrice: price,
          totalPrice: qty * price,
          extra: taxExtra,
        };
      }).filter((item: IPurchaseOrderItem) => item.orderedQuantity > 0); // Ensure only items with quantity > 0

      if (validPoItems.length === 0) {
        EventBus.$emit("show-snackbar", {
          message: `No items with valid quantity to order for ${supplier.name}.`,
          color: "warning"
        });
        group.loading = false;
        return; // Stop if no valid items
      }
      // --- END Sub-task 6.3 ---

      // Create a new purchase order
      const newPo = new PurchaseOrderModel({
        poNumber: this.generatePoNumber(),
        supplierId: isUnknownSupplier ? undefined : Number(supplier.id),
        supplierName: isUnknownSupplier ? undefined : supplier.name,
        status: 'draft',
        orderDate: new Date().toISOString(),
        expectedDeliveryDate: expectedDeliveryDateISOString,
        locationId: Number(this.selectedDeliveryLocationId), // Ensure number
        currencyCode: supplierCurrencyCode,
        items: validPoItems // UPDATED: Use mapped and filtered validPoItems
      });

      await newPo.save();

      // Attempt review request after successful PO creation (non-blocking)
      reviewRequestService.attemptReviewRequest(
        this.store.id,
        'po_created_buying_table'
      );

      // Add to recently created purchase orders (using old state name for now)
      this.recentlyCreatedPOs.unshift(newPo);

      // Remove the created products from the selection
      products.forEach((product: any) => {
        this.removeProductFromSelection(product);
      });

      // Update success state (using old flags for now)
      group.poCreated = true;
      group.poId = newPo.id; // FIXED: use newPo.id

      // Replace emit with EventBus
      EventBus.$emit("show-snackbar", { // UPDATED message
        message: `Purchase Order ${newPo.poNumber || '#' + newPo.id} for ${supplier.name} created.`,
        color: "success"
      });

      if (this.selectedProducts.length === 0) {
        localStorage.removeItem('selectedProducts');
      }

    } catch (error) {
      console.error(`Failed to create purchase order for supplier ${group.supplier.name}`, error);
      EventBus.$emit("show-snackbar", {
        message: `Failed to create purchase order for ${group.supplier.name}`,
        color: "error"
      });
    } finally {
      group.loading = false;
    }
  }

  async fetchSuppliers() {
    try {
      // Get the suppliers list

      await Promise.all([
        this.suppliers.fetch(),
        this.taxTypeList.fetch().catch((e: any) => console.warn('Tax types unavailable:', e)),
      ]);

      // Map suppliers to options for dropdown
      this.supplierOptions = [
        { text: 'All Suppliers', value: 'all' },
        ...this.suppliers.items.map((supplier: any) => ({
          text: supplier.name,
          value: supplier.id
        }))
      ];
    } catch (error) {
      console.error('Failed to fetch suppliers', error);
      this.$emit('error', 'Failed to load suppliers');
    }
  }

  initializeFromLocalStorage() {
    const savedProducts = localStorage.getItem('selectedProducts');
    if (savedProducts) {
      try {
        this.selectedProducts = JSON.parse(savedProducts);
      } catch (e) {
        console.error('Failed to parse saved products', e);
        localStorage.removeItem('selectedProducts');
      }
    }

    const savedFilters = localStorage.getItem(this.buyingTableFiltersKey);
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);

        if (parsed.supplierFilter) {
          this.supplierFilter = parsed.supplierFilter;
        }

        if (parsed.filterColumns && parsed.filterColumns.length > 0) {
          const filterList = new FilterColumnList();
          const initList = new FilterColumnList();
          for (const f of parsed.filterColumns) {
            filterList.add(new FilterColumnModel(f));
            initList.add(new FilterColumnModel(f));
          }
          this.filterColumns = filterList;
          this.initFilterColumns = initList;
        }

        if (parsed.productSegmentId) {
          this.pendingProductSegmentId = parsed.productSegmentId;
        }

        if (parsed.locationFilters && parsed.locationFilters.length > 0) {
          const locationList = new FilterColumnList();
          for (const f of parsed.locationFilters) {
            locationList.add(new FilterColumnModel(f));
          }
          this.activeLocationFilters = locationList;
        }
      } catch (e) {
        localStorage.removeItem(this.buyingTableFiltersKey);
      }
    }
  }

  private async validateSavedProductSegment(): Promise<void> {
    if (!this.pendingProductSegmentId) return;
    try {
      const segment = new ProductSegmentModel({ id: this.pendingProductSegmentId });
      await segment.fetch();
      this.productSegment = segment;
    } catch (e) {
      // Segment no longer exists — clear it from saved filters
      this.pendingProductSegmentId = null;
      try {
        const raw = localStorage.getItem(this.buyingTableFiltersKey);
        if (raw) {
          const saved = JSON.parse(raw);
          delete saved.productSegmentId;
          localStorage.setItem(this.buyingTableFiltersKey, JSON.stringify(saved));
        }
      } catch (_) { /* ignore */ }
    }
  }

  updateTotal(item: any) {
    const quantity = item.reorderQuantity || 0;
    const unitCost = Number(item.unitCost) || 0;
    item.total = quantity * unitCost;

    // Automatically select the item if quantity is entered and item is not already selected
    if (quantity > 0) {
      const isSelected = this.selectedProducts.some(selectedItem => selectedItem.variant_id === item.variant_id);
      if (!isSelected) {
        this.selectedProducts.push(item);
      }
    }
  }

  private formatCurrency(value: any): string {
    const currency = localStorage.getItem('currency') || 'USD';
    if (!value || isNaN(value)) value = 0;
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]+/g, '')) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  }

  // Add a watcher for the supplierFilter property
  @Watch('supplierFilter')
  async onSupplierFilterChanged() {
    if (!this.componentReady || !this.filtersInitialized) return;
    await this.fetchFullData();
  }

  // NEW computed property for filter dropdown, including a special 'All Locations (Show All)' option for UX
  get locationFilterOptions() {
    return [
      { id: 'all-location-option', name: 'All Locations (Show All)' }, // Special value for UX
      ...this.locations // Assuming this.locations is already an array of {id: string, name: string}
    ];
  }

  @Watch("filterData", { deep: true })
  private async onFilterDataChange() {
    if (!this.componentReady || !this.filtersInitialized) return;
    this.filterColumns = this.filterData.filterColumns || new FilterColumnList();
    // Don't overwrite reportParams from ReorderUserParams component
    // Only update reportParams if filterData actually has them (which it shouldn't in this case)
    if (this.filterData.reportParams) {
      this.reportParams = this.filterData.reportParams;
    }
    this.productSegment = this.filterData.productSegment || null;
    await this.fetchFullData();
  }

  public async removeFilter(filter: FilterColumnModel) {
    if (!this.componentReady) return; // Add readiness check
    const index = this.filterColumns.items.findIndex(f => f.code === filter.code);
    if (index > -1) {
      this.filterColumns.items.splice(index, 1);
      await this.fetchFullData();
    }
  }

  public async clearFilters() {
    if (!this.componentReady) return; // Add readiness check
    this.filterColumns = new FilterColumnList();
    this.initFilterColumns = new FilterColumnList();
    this.productSegment = new ProductSegmentModel();
    await this.fetchFullData();
  }

  // Compute products grouped by supplier
  public productsBySupplier: { [key: string]: {
    products: any[],
    supplier: any,
    loading?: boolean,
    poCreated?: boolean,
    poId?: number
  } } = {};

  public generateProductsBySupplier() {
    const grouped: { [key: string]: {
      products: any[],
      supplier: any,
      loading?: boolean,
      poCreated?: boolean,
      poId?: number
    } } = {};

    this.selectedProducts.forEach((product: any) => {
      const supplierId = product.supplier_id || 'unknown';
      if (!grouped[supplierId]) {
        const supplier = this.suppliers.items.find(s => s.id + '' === supplierId + '');
        grouped[supplierId] = {
          products: [],
          supplier: supplier || {
            id: supplierId,
            name: product.supplier || 'Unknown Supplier'
          },
          loading: false,
          poCreated: false,
          poId: undefined
        };
      }
      grouped[supplierId].products.push(product);
    });
    this.productsBySupplier = grouped;
  }

  get hasProducts() {
    return this.selectedProducts.length > 0;
  }

  // Headers for the buying list table
  buyingListTableHeaders: DataTableHeader[] = [
    { text: 'Product', value: 'name', width: '30%', sortable: false, align: 'start' },
    { text: 'Inventory Status', value: 'inventoryStatus', width: '15%', sortable: false, align: 'start' },
    { text: 'Unit Cost', value: 'unitCost', width: '15%', sortable: false, align: 'end' },
    { text: 'Order Qty', value: 'quantity', width: '15%', sortable: false, align: 'center' },
    { text: 'Total', value: 'total', width: '15%', sortable: false, align: 'end' },
    { text: '', value: 'actions', sortable: false, width: '10%', align: 'center' }
  ];

  private get storeCurrency(): string {
    return CurrencyHelper.getStoreCurrency();
  }

  private getSupplierCurrencyCode(supplier: any): string {
    if (!supplier || !supplier.id) return CurrencyHelper.getStoreCurrency();
    const record = this.suppliers.items.find((s: any) => s.id == supplier.id);
    return record?.currency_code || CurrencyHelper.getStoreCurrency();
  }

  private formatSupplierAmount(value: number | string, supplier: any): string {
    const numValue = typeof value === 'string' ? parseFloat(value) : (value || 0);
    return CurrencyHelper.formatCurrencyWithCode(numValue, this.getSupplierCurrencyCode(supplier));
  }

  // Get supplier name from ID
  getSupplierName(supplierId: string): string {
    if (supplierId === 'unknown') return 'Unknown Supplier';

    const supplier = this.suppliers.items.find(s => s.id + '' === supplierId + '');
    return supplier ? supplier.name : 'Unknown Supplier';
  }

  // Remove product from selection
  removeProductFromSelection(product: any) {
    const index = this.selectedProducts.findIndex(p => p.variant_id === product.variant_id);
    if (index !== -1) {
      this.selectedProducts.splice(index, 1);

      // Update productsBySupplier in-place to preserve group state (loading, poCreated, poId)
      const supplierId = product.supplier_id || 'unknown';
      const group = this.productsBySupplier[supplierId];
      if (group) {
        const productIndex = group.products.findIndex(p => p.variant_id === product.variant_id);
        if (productIndex !== -1) {
          group.products.splice(productIndex, 1);
        }
        // Remove empty groups
        if (group.products.length === 0) {
          delete this.productsBySupplier[supplierId];
        }
      }
    }
  }

  // Create buying lists for all suppliers
  async createAllPurchaseOrders() {
    this.creatingAllLists = true;

    try {
      let skippedUnknown = false;
      const groups = Object.values(this.productsBySupplier);

      for (const group of groups) {
        // Skip empty groups and unknown suppliers
        if (!group.products.length) continue;
        if (group.supplier.id === 'unknown') {
          skippedUnknown = true;
          continue;
        }

        try {
          await this.createPurchaseOrderForSupplier(group);
        } catch (error) {
          console.error(`Failed to create buying list for supplier ${group.supplier.name}`, error);
          // Continue with other suppliers even if one fails
          continue;
        }
      }

      // Clear selection and close dialog
      this.selectedProducts = [];
      localStorage.removeItem('selectedProducts');

      // Show success message
      const count = this.recentlyCreatedPOs.length;
      let message = `${count} purchase order${count !== 1 ? 's' : ''} created successfully`;
      if (skippedUnknown) {
        message += '. Some products were skipped because they have no supplier assigned';
      }

      EventBus.$emit("show-snackbar", {
        message,
        color: "success"
      });
    } catch (error) {
      console.error('Failed to create all purchase orders', error);
      EventBus.$emit("show-snackbar", {
        message: "Failed to create all purchase orders",
        color: "error"
      });
    } finally {
      this.creatingAllLists = false;
    }
  }

  formatNumber(value: any, decimals: number = 2): string {
    if (value === null || value === undefined) return '0.00';
    if (typeof value === 'string') value = parseFloat(value);
    if (typeof value !== 'number' || isNaN(value)) return '0.00';
    return value.toFixed(decimals);
  }

  // New UX helper methods - ensure they are public
  public getStockIcon(product: any): string {
    const stock = Number(product['[Actual Inventory Quantity]']) || 0;
    const reorderPoint = Number(product.reorderPoint) || 0;

    if (stock === 0) return 'mdi-alert-circle';
    if (stock <= reorderPoint) return 'mdi-alert';
    return 'mdi-check-circle';
  }

  public getDaysRemainingClass(days: number): string {
    if (days <= 7) return 'error--text font-weight-bold';
    if (days <= 14) return 'warning--text';
    return '';
  }

  // Delegates to the standalone helper in buyingTableColors.ts so the color
  // logic can be unit-tested without instantiating this component. The chip
  // uses a 3-state urgency gradient (grey / orange / red) driven by the
  // reorder due date — see buyingTableColors.ts for branch order. Customer:
  // RPM's TESLA / Kristi, 2026-04-07.
  public getSuggestedQtyColor(item: any): string {
    return getSuggestedQtyColor(item);
  }

  // Tooltip helper methods for urgency indicators
  public getUrgencyColor(item: any): string {
    const suggestedQty = Number(item['[inventory_replenishment]']) || 0;
    const daysRemaining = Number(item.inventoryRemainingDays) || 0;
    const incomingQty = Number(item['[incoming_inventory_quantity]']) || 0;

    // No reorder needed - stock is adequate or incoming covers it
    if (suggestedQty <= 0) return '#4CAF50'; // Green - No action needed

    // Has incoming inventory that may help
    if (incomingQty > 0 && daysRemaining > 7) return '#2196F3'; // Blue - Incoming will help

    if (daysRemaining <= 7) return '#F44336'; // Red - Critical
    if (daysRemaining <= 14) return '#FF9800'; // Orange - Warning
    if (daysRemaining <= 30) return '#2196F3'; // Blue - Attention
    return '#4CAF50'; // Green - Healthy
  }

  public getUrgencyIcon(item: any): string {
    const suggestedQty = Number(item['[inventory_replenishment]']) || 0;
    const daysRemaining = Number(item.inventoryRemainingDays) || 0;
    const incomingQty = Number(item['[incoming_inventory_quantity]']) || 0;

    // No reorder needed
    if (suggestedQty <= 0) return 'mdi-check-circle';

    // Has incoming inventory
    if (incomingQty > 0 && daysRemaining > 7) return 'mdi-truck-delivery';

    if (daysRemaining <= 7) return 'mdi-alert-circle';
    if (daysRemaining <= 14) return 'mdi-alert';
    if (daysRemaining <= 30) return 'mdi-information';
    return 'mdi-check-circle';
  }

  public getUrgencyLabel(item: any): string {
    const suggestedQty = Number(item['[inventory_replenishment]']) || 0;
    const daysRemaining = Number(item.inventoryRemainingDays) || 0;
    const incomingQty = Number(item['[incoming_inventory_quantity]']) || 0;

    // No reorder needed
    if (suggestedQty <= 0) return 'No Reorder Needed';

    // Has incoming inventory that may help
    if (incomingQty > 0 && daysRemaining > 7) return 'Incoming Stock Expected';

    if (daysRemaining <= 7) return 'Critical - Order Now';
    if (daysRemaining <= 14) return 'Low Stock Warning';
    if (daysRemaining <= 30) return 'Reorder Recommended';
    return 'Scheduled Reorder';
  }

  public incrementQuantity(item: any): void {
    if (!item.reorderQuantity) item.reorderQuantity = 0;
    item.reorderQuantity++;
    this.updateTotal(item);
  }

  public decrementQuantity(item: any): void {
    if (item.reorderQuantity > 1) {
      item.reorderQuantity--;
      this.updateTotal(item);
    }
  }

  public applyRecommendedQuantity(item: any): void {
    const suggested = Number(item['[inventory_replenishment]']) || 0;
    item.reorderQuantity = suggested;
    this.updateTotal(item);

    // Show confirmation snackbar
    this.$store.commit('showSnackbar', {
      message: 'Applied suggested quantity',
      color: 'success'
    });
  }

  // Enhanced keyboard navigation
  public handleTableKeydown(event: KeyboardEvent): void {
    // Ctrl/Cmd + A: Select all visible products
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      event.preventDefault();
      this.selectedProducts = [...this.products];
      return;
    }

    // Escape: Clear selection
    if (event.key === 'Escape') {
      this.selectedProducts = [];
      return;
    }

    // Ctrl/Cmd + Enter: Apply all recommendations
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      this.applyAllRecommendations();
      return;
    }
  }

  // Bulk apply all recommendations
  public applyAllRecommendations(): void {
    const itemsToUpdate = this.selectedProducts.length > 0 ? this.selectedProducts : this.products;
    let updatedCount = 0;

    itemsToUpdate.forEach((item: any) => {
      const suggested = Number(item['[inventory_replenishment]']) || 0;
      if (suggested > 0 && suggested !== item.reorderQuantity) {
        item.reorderQuantity = suggested;
        this.updateTotal(item);
        updatedCount++;
      }
    });

    if (updatedCount > 0) {
      this.$store.commit('showSnackbar', {
        message: `Applied recommendations to ${updatedCount} product${updatedCount > 1 ? 's' : ''}`,
        color: 'success'
      });
    }
  }

  formatDate(value: any): string {
    if (!value) return '-';

    try {
      // If it's a timestamp (number)
      if (typeof value === 'number') {
        const date = new Date(value);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      // If it's a string, try to parse it
      if (typeof value === 'string') {
        const date = new Date(value);
        if (isNaN(date.getTime())) return '-'; // Invalid date
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      // If it's already a Date object
      if (value instanceof Date) {
        return value.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }

      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  // Update the handleOptionsUpdate method
  private async handleOptionsUpdate(options: any) {
      if (!this.componentReady) return; // Add readiness check
      // Check if any pagination options actually changed (exclude sorting)
      const optionsChanged =
        Number(options.page) !== Number(this.pageOptions.page) ||
        Number(options.itemsPerPage) !== Number(this.pageOptions.itemsPerPage);

      if (optionsChanged) {
        // Update pageOptions before fetching (preserve existing sort)
        this.pageOptions = {
          page: options.page,
          itemsPerPage: options.itemsPerPage,
          sortBy: this.pageOptions.sortBy, // Keep existing sort
          sortDesc: this.pageOptions.sortDesc, // Keep existing sort
          multiSort: false
        };
        await this.fetchProducts();
      }
  }

  // Handle date range changes from ReorderUserParams component
  public handleDateRangeChange(dates: string[]) {
    // Only extract actual dates (positions [0] and [1]) for API communication
    // The preset string (position [2]) is handled by ReorderUserParams component
    if (dates.length >= 2 && dates[0] && dates[1] && dates[0] !== '' && dates[1] !== '') {
      // Use actual dates for API
      this.dates = [dates[0], dates[1]];
    } else {
      // No valid dates available
      this.dates = [];
    }
    // Note: Don't modify the original dates array to preserve preset information
  }

  // Handle show need reorder now changes from ReorderUserParams component
  public handleShowNeedReorderNowChange(value: boolean) {
    this.showNeedReorderNowOnly = value;
  }

  private async handleDataSyncReady(): Promise<void> {
    this.dataSyncReady = true;
    if (this.componentReady) {
      await this.fetchFullData();
    }
    // If componentReady is false, onReorderParamsLoaded will handle the fetch
  }

  private async onReorderParamsLoaded(loadedParams: any): Promise<void> {
    // Params have been loaded from database, now we can fetch the table data

    // Update our local state with the loaded params
    if (loadedParams.reportParams) {
      this.reportParams = loadedParams.reportParams;
    }

    // Extract date range for API use only (don't interfere with ReorderUserParams component)
    if (loadedParams.dateRange && loadedParams.dateRange.length >= 2 &&
        loadedParams.dateRange[0] && loadedParams.dateRange[1] &&
        loadedParams.dateRange[0] !== '' && loadedParams.dateRange[1] !== '') {
      this.dates = [loadedParams.dateRange[0], loadedParams.dateRange[1]];
    }

    // Update show need reorder now only if available
    if (typeof loadedParams.showNeedReorderNowOnly === 'boolean') {
      this.showNeedReorderNowOnly = loadedParams.showNeedReorderNowOnly;
    }

    // Set componentReady to true NOW that params are actually loaded
    this.componentReady = true;

    // Now fetch the table data with the loaded parameters
    await this.fetchFullData();
    this.filtersInitialized = true;
    this.initialLoading = false;
  }

  private onReorderParamsSaved(params: any): void {
    // Optionally show success notification
  }

  private onReorderParamsSaveError(error: any): void {
    console.error('Failed to save reorder params:', error);
    // Optionally show error notification
    // EventBus.$emit('show-error', 'Failed to save preferences. Changes are saved locally.');
  }


  // NEW Method to view a Purchase Order
  private viewPurchaseOrder(poId: number | undefined | null) {
    if (poId) {
      this.$router.push(`/purchase-orders/${poId}`);
    } else {
      console.error('Purchase Order ID is null or undefined');
    }
  }

  // Computed property for Delivery Location dropdown items (uses actual locations)
  get deliveryLocationOptions() {
    return this.locations.map(loc => ({ id: loc.id, name: loc.name }));
  }

  // Validation rule for delivery location
  get deliveryLocationRule() {
    return [(v: string | null) => !!v || 'Delivery location is required'];
  }

  // NEW method to clear all selected products
  public clearAllSelectedProducts(): void {
    this.selectedProducts = [];
    localStorage.removeItem('selectedProducts');
  }

  // NEW method to select all products
  public selectAllProducts(): void {
    this.selectedProducts = [...this.products];
  }

  // Show product details dialog
  public showProductDetails(product: any): void {
    this.selectedProductForDetails = product;
    this.productDetailsDialog = true;
  }

  // Show incoming inventory breakdown dialog
  public async showIncomingBreakdown(item: any): Promise<void> {
    // Validate variantId before opening dialog
    const variantId = item.variant_id || item['[variant_id]'];
    if (!variantId) {
      console.error('No variant ID found for incoming breakdown');
      EventBus.$emit('show-snackbar', {
        message: 'Unable to load incoming inventory: missing variant ID',
        color: 'error'
      });
      return;
    }

    // Open dialog and set loading state
    this.selectedVariantForIncoming = item;
    this.incomingBreakdownDialog = true;
    this.incomingBreakdownLoading = true;
    this.incomingBreakdownData = null;

    try {
      const purchaseOrdersCollection = new PurchaseOrders();
      this.incomingBreakdownData = await purchaseOrdersCollection.getIncomingByVariant(String(variantId));
    } catch (error) {
      console.error('Failed to fetch incoming breakdown:', error);
      EventBus.$emit('show-snackbar', {
        message: 'Failed to load incoming inventory breakdown',
        color: 'error'
      });
    } finally {
      this.incomingBreakdownLoading = false;
    }
  }

  // Get location name by ID for incoming breakdown dialog
  public getLocationNameById(locationId: number): string {
    if (!locationId) return '-';
    const location = this.locations.find(loc => Number(loc.id) === Number(locationId));
    return location ? location.name : '-';
  }

  // Handle row click to show product details
  public handleRowClick(item: any, row: any): void {
    // Prevent opening dialog when clicking on checkbox
    if (row?.event?.target?.closest('.v-data-table__checkbox')) {
      return;
    }
    this.showProductDetails(item);
  }

  // Format values for display in the details dialog
  public formatDetailValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') return 'Not Set';

    switch (key) {
      case 'cost':
      case 'price':
      case 'margin':
      case 'replenishment_cost':
      case 'replenishment_revenue':
      case 'replenishment_profit':
      case 'net_sales':
      case 'sales_contribution':
      case 'sales_contribution_acc':
        return this.formatCurrency(value);
      case 'reorder_date':
      case 'coverage_start':
      case 'coverage_end':
        return this.formatDate(value);
      case 'days_remaining':
      case 'coverage_days':
      case 'lead_time':
      case 'cycle':
      case 'min_cover_days':
      case 'max_cover_days':
      case 'safety_cover_days':
      case 'days_to_reorder':
      case 'out_of_stock_days':
        return `${value} days`;
      case 'inventory':
      case 'incoming':
      case 'total_sold':
      case 'suggested':
      case 'order_qty':
      case 'case_qty':
      case 'min_order':
      case 'max_order':
      case 'fixed_order':
      case 'variant_min':
      case 'variant_max':
      case 'min_inventory':
      case 'max_inventory':
      case 'reorder_point':
      case 'safety_stock':
      case 'forecast_lead_demand':
      case 'forecast_next_demand':
      case 'forecast_over_under':
      case 'forecast_lost_sales':
        return this.formatNumber(value, 0);
      case 'sales_rate':
      case 'forecast_daily':
      case 'adjusted_daily':
        return `${this.formatNumber(value, 2)} units/day`;
      case 'priority':
        return String(value).toUpperCase();
      default:
        return String(value);
    }
  }

  // Generate formula with actual values for better understanding
  public getFormulaWithValues(item: any): string {
    if (!item.formula || !this.selectedProductForDetails) return item.formula || '';

    const product = this.selectedProductForDetails;
    let formulaWithValues = item.formula;

    // Helper function to format value or show "Not Set" if null
    const formatValue = (val: any, decimals: number = 0, suffix: string = ''): string => {
      if (val === null || val === undefined || val === '') {
        return 'Not Set';
      }
      return this.formatNumber(val, decimals) + suffix;
    };

    // Helper function to format value with days suffix
    const formatDays = (val: any): string => {
      if (val === null || val === undefined || val === '') {
        return 'Not Set';
      }
      return `${val} days`;
    };

    switch (item.key) {
      case 'days_remaining':
        const inventory = formatValue(product.availableQuantity, 0);
        const dailySales = formatValue(product.forecastUnitsSoldPerDay, 2);
        formulaWithValues = `${inventory} / ${dailySales}`;
        break;
      case 'forecast_lead_demand':
        const forecastDaily = formatValue(product.forecastUnitsSoldPerDay, 2);
        const vendorLeadTime = product.vendorLeadTime ?? 30; // Default to 30 days as per the items definition
        const leadTime = formatDays(vendorLeadTime);
        formulaWithValues = `${forecastDaily} × ${leadTime}`;
        break;
      case 'forecast_daily_coverage':
        const forecastCoverageValue = formatValue(product.forecastDailySalesCoverage || product.forecastUnitsSoldPerDay, 2);
        formulaWithValues = `${forecastCoverageValue} units/day (based on seasonality adjustments)`;
        break;
      case 'forecast_next_demand':
        const forecastDailyCov = formatValue(product.forecastDailySalesCoverage || product.forecastUnitsSoldPerDay, 2);
        const coverageDays = formatDays(product.coverageDays);
        formulaWithValues = `${forecastDailyCov} × ${coverageDays}`;
        break;
      case 'forecast_inventory_need':
        const nextDemand = formatValue(Math.round(product.forecastNextCycleDemand ?? 0), 0);
        const safetyStock = formatValue(Math.round(product.safetyStock ?? 0), 0);
        formulaWithValues = `${nextDemand} + ${safetyStock}`;
        break;
      case 'reorder_point':
        const safety = formatValue(product.safetyStock, 0);
        const dailyForecast = formatValue(product.forecastUnitsSoldPerDay, 2);
        const vendorLead = product.vendorLeadTime ?? 30; // Default to 30 days as per the items definition
        formulaWithValues = `${safety} + (${dailyForecast} × ${vendorLead})`;
        break;
      case 'coverage_start':
        const reorderDate = product.reorderDate ? this.formatDate(product.reorderDate) : 'Not Set';
        const vendorLeadTimeForCoverage = product.vendorLeadTime ?? 30; // Default to 30 days
        const leadDays = formatDays(vendorLeadTimeForCoverage);
        formulaWithValues = `${reorderDate} + ${leadDays}`;
        break;
      case 'coverage_end':
        const coverageStart = product.coverageStartDate ? this.formatDate(product.coverageStartDate) : 'Not Set';
        const coverDays = formatDays(product.coverageDays);
        formulaWithValues = `${coverageStart} + ${coverDays}`;
        break;
      case 'suggest_reorder_quantity':
        const inventoryNeed = (product.safetyStock ?? 0) + (product.forecastNextCycleDemand ?? 0);
        const inventoryNeedStr = inventoryNeed === 0 && (!product.safetyStock && !product.forecastNextCycleDemand) ? 'Not Set' : formatValue(inventoryNeed, 0);
        const incoming = formatValue(product.incomingQuantity, 0);
        formulaWithValues = `${inventoryNeedStr} - ${incoming}`;
        break;
      case 'suggested':
        // Updated formula for Suggested Quantity: [Forecast Inventory Need] - [Incoming Inventory]
        const inventoryNeedCalc = Math.round((product.safetyStock ?? 0) + (product.forecastNextCycleDemand ?? 0));
        const inventoryNeedValue = formatValue(inventoryNeedCalc, 0);
        const incomingValue = formatValue(product.incomingQuantity, 0);
        formulaWithValues = `${inventoryNeedValue} - ${incomingValue}`;
        break;
      // case 'safety_stock':
      //   // Safety Stock = MIN(Min Inventory Qty, Min Stock Cover Days × Forecast Daily)
      //   const minInvQty = product.minInventoryQuantity;
      //   const minCoverDays = product.minStockCoverDays;
      //   const safetyDailyForecast = product.forecastUnitsSoldPerDay;

      //   if (minInvQty !== null && minInvQty !== undefined && minInvQty > 0) {
      //     if (minCoverDays !== null && minCoverDays !== undefined && safetyDailyForecast) {
      //       const coverCalc = Number(minCoverDays) * Number(safetyDailyForecast);
      //       formulaWithValues = `( ${formatValue(minInvQty, 0)} OR ${formatValue(minCoverDays, 0)} × ${formatValue(safetyDailyForecast, 2)} ) = (${formatValue(minInvQty, 0)} OR ${formatValue(coverCalc, 0)})`;
      //     } else {
      //       formulaWithValues = `${formatValue(minInvQty, 0)} (using Min Inventory Quantity)`;
      //     }
      //   } else if (minCoverDays !== null && minCoverDays !== undefined && safetyDailyForecast) {
      //     formulaWithValues = `${formatValue(minCoverDays, 0)} × ${formatValue(safetyDailyForecast, 2)}`;
      //   } else {
      //     formulaWithValues = 'Not configured';
      //   }
      //   break;
      case 'coverage_days':
        // Reorder Coverage Days = Order Cycle
        const orderCycle = product.orderCycle || product.reorderCycle || product.coverageDays;
        formulaWithValues = `Order Cycle (${formatValue(orderCycle, 0)} days)`;
        break;
      case 'case_quantity':
        // Case Quantity is a configured value
        const caseQtyValue = product['[case_quantity]'];
        if (caseQtyValue && caseQtyValue > 0) {
          formulaWithValues = `${formatValue(caseQtyValue, 0)} units per case (configured value)`;
        } else {
          formulaWithValues = 'Not configured';
        }
        break;
      // case 'suggested_rounded':
      //   // Rounded = CEILING(Suggested / Case Qty) × Case Qty
      //   const suggestedQty = product['[inventory_replenishment]'] || 0;
      //   const caseQuantity = product['[case_quantity]'];

      //   if (caseQuantity && caseQuantity > 0) {
      //     const numCases = Math.ceil(suggestedQty / caseQuantity);
      //     formulaWithValues = `CEILING(${formatValue(suggestedQty, 0)} ÷ ${formatValue(caseQuantity, 0)}) × ${formatValue(caseQuantity, 0)} = ${numCases} × ${formatValue(caseQuantity, 0)}`;
      //   } else {
      //     // No case quantity, so no rounding
      //     formulaWithValues = `${formatValue(suggestedQty, 0)} (no case rounding)`;
      //   }
      //   break;
    }

    return formulaWithValues;
  }

  // Generate formula with styled HTML for "Not Set" values
  public getFormulaWithValuesStyled(item: any): string {
    const formula = this.getFormulaWithValues(item);
    const result = this.formatDetailValue(item.key, item.value);

    // Replace "Not Set" with styled span
    let styledFormula = formula.replace(/Not Set/g, '<span class="formula-not-set">Not Set</span>');

    // Add the result with appropriate styling
    const resultClass = (item.value === null || item.value === undefined || item.value === '')
      ? 'formula-result formula-result-not-set'
      : 'formula-result';

    styledFormula += ` <span class="${resultClass}">= ${result}</span>`;

    return styledFormula;
  }

  // Get CSS class for values based on their significance
  public getValueClass(key: string, value: any): string {
    switch (key) {
      case 'days_remaining':
      case 'days_to_reorder':
        if (Number(value) <= 7) return 'error--text font-weight-bold';
        if (Number(value) <= 14) return 'warning--text';
        return '';
      case 'inventory':
      //case 'min_inventory':
        if (Number(value) === 0) return 'error--text font-weight-bold';
        return '';
      case 'suggested':
      case 'forecast_lead_demand':
      case 'forecast_next_demand':
        return 'primary--text font-weight-bold';
      case 'out_of_stock_days':
        if (Number(value) > 0) return 'error--text font-weight-bold';
        return 'success--text';
      case 'priority':
        if (String(value).toLowerCase() === 'high') return 'error--text font-weight-bold';
        if (String(value).toLowerCase() === 'medium') return 'warning--text';
        return '';
      case 'replenishment_profit':
      case 'net_sales':
      case 'sales_contribution':
        return 'success--text font-weight-medium';
      default:
        return '';
    }
  }

  // Consolidated subgroup configuration for forecast properties
  private readonly subgroupConfig: { [key: string]: { icon: string; color: string; title: string } } = {
    'settings': { icon: 'mdi-cog-outline', color: '#607D8B', title: 'Settings' },
    'sales_rate': { icon: 'mdi-chart-line', color: '#1976D2', title: 'Sales Rate' },
    'demand': { icon: 'mdi-chart-bar', color: '#FF9800', title: 'Demand Forecast' },
    'projection': { icon: 'mdi-package-variant', color: '#4CAF50', title: 'Inventory Projection' },
    'recommendation': { icon: 'mdi-cart-check', color: '#E91E63', title: 'Order Recommendation' }
  };

  public getSubgroupIcon(subgroup: string): string {
    return this.subgroupConfig[subgroup]?.icon || 'mdi-circle-small';
  }

  public getSubgroupColor(subgroup: string): string {
    return this.subgroupConfig[subgroup]?.color || '#666';
  }

  public getSubgroupTitle(subgroup: string): string {
    return this.subgroupConfig[subgroup]?.title || subgroup;
  }

  // Add method to handle location filter update
  private async handleLocationFilterUpdate(filterList: FilterColumnList): Promise<void> {
    this.activeLocationFilters = filterList;
    await this.fetchFullData();
  }


  private generatePoNumber(): string {
    const prefix = 'PO';
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const randomNumber = Math.floor(Math.random() * 90000 + 10000); // 5-digit random number
    return `${prefix}${date}-${randomNumber}`;
  }

  // Handler for ReportSort component sort changes
  public onSortChanged(event: { sortBy: string[], sortDesc: boolean[] }): void {
    this.pageOptions.sortBy = event.sortBy;
    this.pageOptions.sortDesc = event.sortDesc;
    this.fetchProducts();
  }

  // Export methods
  public showExportDialog(): void {
    this.exportDialog = true;
  }

  public updateDownloadFileExt(fileExt: string): void {
    this.downloadFileExt = fileExt;
  }

  public handleDownloadMessage(event: { type: string; message: string }): void {
    EventBus.$emit('show-snackbar', {
      message: event.message,
      color: event.type === 'error' ? 'error' : 'success',
    });
  }

  public async handleExport(emails: string[], selectedDriveFolder: string, downloadName: string = ''): Promise<void> {
    try {
      // Get current filters
      const { filters, supplierId } = this.getCommonFilters();

      // Create supplier model to fetch the report
      const supplier = new SupplierModel({ id: parseInt(supplierId) });
      const supplierFilter = supplier.id ? 'eq' : 'all';

      // Build report options
      const reportOptions: any = {
        initialFilterColumns: filters,  // Changed from 'filters' to 'initialFilterColumns'
        initialReportParams: this.reportParams,  // Changed from 'reportParams' to 'initialReportParams'
        //columns: this.reportColumns,  // Changed from 'reportColumns' to 'columns'
        showNeedReorderNowOnly: this.showNeedReorderNowOnly,
        suggestFutureReorderQuantity: !this.showNeedReorderNowOnly,
        productSegment: this.productSegment,
        skipped: true
      };

      reportOptions.columns = [
          { code: 'variant_image', dataType: 'image', name: 'Image' },
          { code: 'Item[Item Description]', dataType: 'string', name: "Product" },
          { code: 'product_sku', dataType: 'string', name: 'SKU' },
          { code: 'product_barcode', dataType: 'string', name: 'Barcode' },
          { code: 'supplier', dataType: 'string', name: 'Supplier' },
          { code: '[average_daily_units_sold_param]', dataType: 'number', name: 'Historical Units Sold Per Day' },
          { code: '[average_daily_units_sold_forecast]', dataType: 'number', name: 'Forecast Units Sold Per Day' },
          { code: '[Actual Inventory Quantity]', dataType: 'number', name: 'Inventory Quantity' },
          { code: '[stock_days_inventory_planning]', dataType: 'number', name: 'Inventory Remaining Days' },
          { code: '[incoming_inventory_quantity]', dataType: 'number', name: 'Incoming Inventory' },
          { code: '[inventory_replenishment]', dataType: 'number', name: 'Reorder Quantity' },
          { code: '[reorder_date]', dataType: 'number', name: 'Reorder Date' },
          { code: '[reorder_priority]', dataType: 'number', name: 'Reorder Point' },
          { code: '[vendor_lead_time]', dataType: 'number', name: 'Vendor Leadtime' },
          { code: '[safety_stock_level]', dataType: 'number', name: 'Safety Stock' },
          { code: '[reorder_cycle]', dataType: 'number', name: 'Reorder Cycle' }
        ];
      // Add date range if available
      if (this.dates && this.dates.length === 2) {
        reportOptions.dateRange = {
          start: this.dates[0],
          end: this.dates[1],
        };
      }

      // Add page options including sorting
      if (this.pageOptions) {
        reportOptions.pageOptions = {
          ...this.pageOptions,
          itemsPerPage: -1,  // Get all items for export
        };
      }

      // Fetch the report model with proper initialization
      const report: ReportModel = await supplier.fetchProductsWithOptions(
        'inventory_replenishment',
        supplierFilter,
        reportOptions
      );

      // Set export specific parameters
      report.downloadFileExt = this.downloadFileExt;
      report.total = this.totalItems.toString();

      // Call the download method directly - the report already has all filters and data
      await report.download(emails, true, selectedDriveFolder, downloadName || 'Buying Table Export');

      // Close the dialog
      this.exportDialog = false;

      // Show success notification
      EventBus.$emit('show-snackbar', {
        message: 'The export is being prepared and will be sent to your email shortly.',
        color: 'success',
      });

      // Track the export event
      try {
        const mixpanel = (window as any).mixpanel;
        if (mixpanel) {
          mixpanel.track('export_buying_table', {
            fileFormat: this.downloadFileExt,
            totalRecords: this.totalItems,
            hasSupplierFilter: !!this.supplierFilter,
            hasDateFilter: this.dates.length > 0,
          });
        }
      } catch (error) {
        // Failed to track export event
      }
    } catch (error: any) {
      // Show error message
      EventBus.$emit('show-snackbar', {
        message: error.message || 'Failed to export data. Please try again.',
        color: 'error',
      });
      // Export error occurred
    }
  }

  // Add new method to show sort arrow based on sort state
  private showSortArrow(columns: string[], isDesc: boolean): boolean {
    if (!this.pageOptions.sortBy || this.pageOptions.sortBy.length === 0 ||
        !this.pageOptions.sortDesc || this.pageOptions.sortDesc.length === 0) {
      return false;
    }

    // Check if any of the provided columns are currently being sorted
    for (let i = 0; i < this.pageOptions.sortBy.length; i++) {
      const sortBy = this.pageOptions.sortBy[i];
      const sortDesc = this.pageOptions.sortDesc[i];

      // Check if this sorted column is in our array of columns
      if (columns.includes(sortBy) && sortDesc === isDesc) {
        return true;
      }
    }

    return false;
  }
}
